import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { getFieldValidator } from './personal.validators';
import { setRegion } from '../region/regionSlice';
import { config } from '../../config';
import type { RootState } from '../../configuration/setup/store';

export type PersonalInfoField = keyof PersonalInfoState;

export type PersonalToggles = keyof PersonalCheckBoxes;

// TODO: clean up the interfaces
export interface PersonalCheckBoxes {
    newsletterApproved: boolean;
    termsAccepted: boolean;
}

export interface PersonalInfoState {
    firstName: string;
    firstNameValidationMessage?: string | null;
    firstNameTouched?: boolean;
    lastName: string;
    lastNameValidationMessage?: string | null;
    lastNameTouched?: boolean;
    email: string;
    emailValidationMessage?: string | null;
    emailTouched?: boolean;
    phoneNumber?: string;
    phoneNumberValidationMessage?: string | null;
    phoneNumberTouched?: boolean;
    newsletterApproved: boolean;
    termsAccepted: boolean;
}

const initialState: PersonalInfoState = {
    firstName: '',
    firstNameValidationMessage: '',
    firstNameTouched: false,
    lastName: '',
    lastNameValidationMessage: '',
    lastNameTouched: false,
    email: '',
    emailValidationMessage: '',
    emailTouched: false,
    phoneNumber: '',
    phoneNumberValidationMessage: null,
    phoneNumberTouched: false,
    newsletterApproved: false,
    termsAccepted: false,
};

const personalInfoSlice = createSlice({
    name: 'personal',
    initialState,
    reducers: {
        setPersonalInfoField: (state, action: PayloadAction<{ fieldName: PersonalInfoField; value: string }>) => {
            const { fieldName, value } = action.payload;
            const nextState = {
                ...state,
                [fieldName]: value,
            };

            const validProperty = `${fieldName}ValidationMessage`;
            const validator = getFieldValidator(fieldName) || (() => true);
            return {
                ...nextState,
                [validProperty]: validator(value),
            };
        },
        touchPersonalInfoField: (state, action: PayloadAction<PersonalInfoField>) => {
            const fieldName = action.payload;

            // we dynamically add a "[fieldName]Touched" property with value true
            // which is otherwise "undefined" and because of that false
            const propertyName = `${fieldName}Touched`;

            return {
                ...state,
                [propertyName]: true,
            };
        },
        togglePersonalCheckbox: (state, action: PayloadAction<PersonalToggles>) => {
            const fieldName = action.payload;

            return {
                ...state,
                [fieldName]: !state[fieldName],
            };
        },
    },
    extraReducers: builder => {
        builder.addCase(setRegion, state => {
            if (config.isTestTenantMode) {
                state.firstName = 'TestUserFirstName';
                state.firstNameValidationMessage = null;
                state.lastName = 'TestUserLastName';
                state.lastNameValidationMessage = null;
            }
        });
    },
});

export const { setPersonalInfoField, touchPersonalInfoField, togglePersonalCheckbox } = personalInfoSlice.actions;

export const personalInfoReducer = personalInfoSlice.reducer;

export const getPersonalInfoEmail = (state: RootState) => state.personalInfo.email;
